import React, { useEffect, useRef, useState } from 'react';
import { 
  FaUser, FaEnvelope, FaPhone, FaBookOpen, 
  FaRegClock, FaCheckCircle, FaArrowRight, 
  FaPaperPlane, FaInfoCircle, FaTimes,
  FaGraduationCap, FaQuestionCircle
} from 'react-icons/fa';
import { MdLocationOn, MdMessage } from 'react-icons/md';

const EnquiryForm = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    course: '',
    message: ''
  });
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Check if popup was shown in this session
  useEffect(() => {
    const popupShown = sessionStorage.getItem('enquiryPopupShown');
    if (!popupShown) {
      // Show popup after 2 seconds on page load/refresh
      const timer = setTimeout(() => {
        setShowPopup(true);
        sessionStorage.setItem('enquiryPopupShown', 'true');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    setShowPopup(false);
    setTimeout(() => {
      setFormSubmitted(false);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        course: '',
        message: ''
      });
    }, 3000);
  };

  const handlePopupSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    setShowPopup(false);
    setTimeout(() => {
      setFormSubmitted(false);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        course: '',
        message: ''
      });
    }, 3000);
  };

  const courses = [
    { value: "", label: "Select Course" },
    { value: "bcom", label: "Bachelor of Commerce (B.Com)" },
    { value: "bsc", label: "Bachelor of Science (B.Sc)" },
    { value: "ba", label: "Bachelor of Arts (B.A)" }
  ];

  return (
    <>
      <section ref={sectionRef} className="w-full bg-gradient-to-br from-yellow-50 via-white to-blue-50 py-16">
        <div className="w-full px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            
            {/* Decorative Top */}
            <div className="flex justify-center gap-3 mb-6">
              <div className="w-12 h-1 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full"></div>
              <div className="w-8 h-1 bg-gradient-to-r from-orange-400 to-orange-500 rounded-full"></div>
              <div className="w-6 h-1 bg-gradient-to-r from-blue-400 to-blue-500 rounded-full"></div>
            </div>

            {/* Heading */}
            <div className="text-center mb-10">
              <h2 className="text-4xl md:text-5xl font-bold mb-3">
                <span className="bg-gradient-to-r from-yellow-500 via-orange-500 to-blue-500 bg-clip-text text-transparent">
                  Enquiry Form
                </span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500 rounded-full mx-auto"></div>
              <p className="text-gray-600 text-sm mt-4">
                Fill the form below and we'll get back to you within 24 hours
              </p>
            </div>

            {/* Form and Info Grid */}
            <div className={`grid md:grid-cols-2 gap-8 transition-all duration-500 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
              
              {/* Left Side - Form */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500 p-5">
                  <h3 className="text-white font-bold text-xl text-center">Get in Touch</h3>
                  <p className="text-white/90 text-sm text-center mt-1">We'd love to hear from you</p>
                </div>

                <div className="p-6">
                  {formSubmitted ? (
                    <div className="text-center py-8">
                      <FaCheckCircle className="text-green-500 text-5xl mx-auto mb-3" />
                      <p className="text-gray-800 font-semibold text-lg">Enquiry Submitted!</p>
                      <p className="text-gray-500 text-sm mt-2">We'll contact you soon.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="block text-gray-700 text-sm font-semibold mb-2">
                          <FaUser className="inline mr-2 text-yellow-500 text-xs" />
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleChange}
                          placeholder="Enter your full name"
                          required
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-gray-700 text-sm font-semibold mb-2">
                          <FaEnvelope className="inline mr-2 text-yellow-500 text-xs" />
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          required
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-gray-700 text-sm font-semibold mb-2">
                          <FaPhone className="inline mr-2 text-yellow-500 text-xs" />
                          Mobile Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+91 1234567890"
                          required
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-gray-700 text-sm font-semibold mb-2">
                          <FaBookOpen className="inline mr-2 text-yellow-500 text-xs" />
                          Interested Course <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="course"
                          value={formData.course}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all bg-white"
                        >
                          {courses.map((course, idx) => (
                            <option key={idx} value={course.value}>{course.label}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-gray-700 text-sm font-semibold mb-2">
                          <MdMessage className="inline mr-2 text-yellow-500 text-sm" />
                          Your Message
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows="4"
                          placeholder="Write your message or query here..."
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all resize-none"
                        ></textarea>
                      </div>

                      <button
                        type="submit"
                        className="w-full py-3 bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500 text-white font-semibold rounded-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 group"
                      >
                        Submit Enquiry
                        <FaPaperPlane className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                      </button>

                      <p className="text-gray-400 text-xs text-center flex items-center justify-center gap-1 mt-3">
                        <FaRegClock className="text-xs" />
                        We'll respond within 24 hours
                      </p>
                    </form>
                  )}
                </div>
              </div>

              {/* Right Side - Info Cards */}
              <div className="space-y-5">
                <div className="bg-white rounded-2xl p-5 shadow-md">
                  <h3 className="font-bold text-gray-800 text-lg mb-3 flex items-center gap-2">
                    <FaInfoCircle className="text-yellow-500" />
                    Contact Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                        <FaPhone className="text-yellow-500 text-sm" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Phone</p>
                        <p className="text-gray-800 text-sm font-semibold">+91 7080 100 333</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                        <FaEnvelope className="text-yellow-500 text-sm" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Email</p>
                        <p className="text-gray-800 text-sm font-semibold">bnsmahilamahavidyalaya@gmail.com</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                        <MdLocationOn className="text-yellow-500 text-sm" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Address</p>
                        <p className="text-gray-800 text-sm font-semibold">Akhari Bai-Pass, Varanasi, UP 221011</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-5 shadow-md">
                  <h3 className="font-bold text-gray-800 text-lg mb-3 flex items-center gap-2">
                    <FaRegClock className="text-yellow-500" />
                    Working Hours
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 text-sm">Monday - Friday</span>
                      <span className="text-gray-800 text-sm font-semibold">9:00 AM - 5:00 PM</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 text-sm">Saturday</span>
                      <span className="text-gray-800 text-sm font-semibold">9:00 AM - 2:00 PM</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 text-sm">Sunday</span>
                      <span className="text-gray-800 text-sm font-semibold">Closed</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500 rounded-2xl p-5 text-white">
                  <h3 className="font-bold text-lg mb-3">Why Choose Us?</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <FaCheckCircle className="text-white text-sm" />
                      <span className="text-sm">Quality Education</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FaCheckCircle className="text-white text-sm" />
                      <span className="text-sm">Experienced Faculty</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FaCheckCircle className="text-white text-sm" />
                      <span className="text-sm">Modern Infrastructure</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FaCheckCircle className="text-white text-sm" />
                      <span className="text-sm">Placement Assistance</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Decorative */}
            <div className="mt-10 flex justify-center gap-2">
              <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Popup Modal */}
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto animate-slideUp">
            {/* Popup Header */}
            <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500 p-4 rounded-t-2xl flex justify-between items-center">
              <div className="flex items-center gap-2">
                <FaQuestionCircle className="text-white text-xl" />
                <h3 className="text-white font-bold text-lg">Quick Enquiry</h3>
              </div>
              <button 
                onClick={() => setShowPopup(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>

            {/* Popup Body */}
            <div className="p-5">
              <p className="text-gray-600 text-sm mb-4 text-center">
                Fill this quick form and our team will contact you soon!
              </p>

              <form onSubmit={handlePopupSubmit} className="space-y-3">
                <div>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Full Name *"
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20"
                  />
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email Address *"
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20"
                  />
                </div>

                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Mobile Number *"
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20"
                  />
                </div>

                <div>
                  <select
                    name="course"
                    value={formData.course}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 bg-white"
                  >
                    {courses.map((course, idx) => (
                      <option key={idx} value={course.value}>{course.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Your Message"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 resize-none"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full py-2.5 bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500 text-white font-semibold rounded-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 group"
                >
                  Submit Enquiry
                  <FaPaperPlane className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </button>
              </form>

              <p className="text-gray-400 text-xs text-center mt-3">
                We'll get back to you within 24 hours
              </p>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(50px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-bounce {
          animation: bounce 1s ease-in-out infinite;
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default EnquiryForm;